import React, { useMemo, useState, useEffect } from "react";

export default function SvgLogoGeneratorReact() {
  // ---- style.css inlined (from user's file) ----
  const css = `:root{--bg:#0b0f14;--panel:#0f1620;--card:#121b27;--muted:#90a3b5;--text:#e6f0fb;--acc:#4cc9f0;--ok:#22c55e}
*{box-sizing:border-box}
html,body{height:100%}
body{margin:0;background:linear-gradient(180deg,#0b0f14,#0a0e13);color:var(--text);font:14px/1.45 system-ui,-apple-system,Segoe UI,Roboto,Ubuntu,Cantarell,"Helvetica Neue",Arial}
.app{display:grid;grid-template-columns:470px 1fr;grid-template-rows:64px 1fr;gap:12px;height:100%;padding:16px}
header{grid-column:1/3;background:var(--panel);border:1px solid #1c2735;border-radius:16px;padding:10px 14px;display:flex;align-items:center;justify-content:space-between}
header .title{font-weight:800;letter-spacing:.3px}
.side{background:var(--panel);border:1px solid #1c2735;border-radius:16px;padding:14px;overflow:auto}
.main{background:var(--panel);border:1px solid #1c2735;border-radius:16px;padding:14px;display:grid;grid-template-rows:1fr 280px;gap:12px;min-height:0}
.row{display:flex;align-items:center;gap:8px;margin:8px 0}
.row label{width:62px;color:#a9bfd2}
input[type="text"], input[type="number"], select{flex:1;min-width:0;background:#0c141e;border:1px solid #233347;color:var(--text);border-radius:10px;padding:8px 10px}
input[type="color"]{width:44px;height:32px;border:none;background:transparent;padding:0}
.grid{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.btn{background:#101a27;border:1px solid #1e2a3a;color:var(--text);padding:8px 12px;border-radius:10px;cursor:pointer}
.btn:hover{border-color:#2a3a51}
.btn.acc{background:var(--acc);color:#04121b;border-color:#2fb8e5}
.btn.ok{background:var(--ok);color:#041b11;border-color:#1a9b4b}
.preview{background:var(--card);border:1px dashed #263549;border-radius:14px;display:flex;align-items:center;justify-content:center;position:relative;overflow:auto}
.out{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.card{background:#0e1722;border:1px solid #1c2735;border-radius:12px;padding:10px;display:flex;flex-direction:column}
textarea{min-height:0;flex:1;background:#0c141e;border:1px solid #233347;border-radius:10px;color:#d9e7f5;padding:10px;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
.swatch{display:grid;grid-template-columns:repeat(10,18px);gap:6px}
.swatch button{width:18px;height:18px;border-radius:4px;border:1px solid #294059;cursor:pointer}
.toolbar{display:flex;gap:8px;flex-wrap:wrap}`;

  // ---- fonts, layouts, palettes, glyphs (from user's script) ----
  const fontStacks = useMemo(() => [
    'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
    'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
    'Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
    'Roboto, Helvetica Neue, Arial, sans-serif',
    'Helvetica Neue, Helvetica, Arial, sans-serif',
    'Arial, Helvetica, sans-serif',
    'Verdana, Geneva, Tahoma, sans-serif',
    'Tahoma, Geneva, Verdana, sans-serif',
    'Trebuchet MS, Segoe UI, Tahoma, sans-serif',
    'Noto Sans, Segoe UI, Helvetica, Arial, sans-serif',
    'Ubuntu, Cantarell, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
    'Cantarell, Ubuntu, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
    'Avenir Next, Avenir, Segoe UI, Helvetica, Arial, sans-serif',
    'Avenir, Segoe UI, Helvetica, Arial, sans-serif',
    'Gill Sans, Gill Sans MT, Calibri, Trebuchet MS, sans-serif',
    'Optima, Segoe, Segoe UI, Candara, Calibri, Arial, sans-serif',
    'Calibri, Candara, Segoe, Segoe UI, Optima, Arial, sans-serif',
    'Franklin Gothic Medium, Arial Narrow, Arial, sans-serif',
    'Lucida Sans, Lucida Grande, Lucida Sans Unicode, Geneva, Verdana, sans-serif',
    'Georgia, Cambria, Times, Times New Roman, serif',
    'Times New Roman, Times, Georgia, serif',
    'Garamond, Baskerville, Baskerville Old Face, Times New Roman, serif',
    'Palatino Linotype, Palatino, Book Antiqua, Georgia, serif',
    'Didot, Bodoni MT, Didot LT STD, Hoefler Text, Garamond, serif',
    'Cambria, Georgia, serif',
    'Courier New, Courier, ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    'Consolas, Menlo, Monaco, ui-monospace, SFMono-Regular, monospace',
    'Menlo, Monaco, Consolas, ui-monospace, SFMono-Regular, monospace',
    'Fira Code, Consolas, Menlo, Monaco, ui-monospace, monospace'
  ], []);

  const layouts = useMemo(() => [
    'icon','left','right','top','bottom','stacked-center','stacked-left','stacked-right','badge-center','badge-left','badge-right','split-50-50','split-40-60','overlap'
  ], []);

  const palettes = useMemo(() => [
    ['#4cc9f0','#a78bfa','#ffffff','#0b0f14'],
    ['#22d3ee','#06b6d4','#0b0f14','#ffffff'],
    ['#f59e0b','#ef4444','#0b0f14','#ffffff'],
    ['#34d399','#22c55e','#06251a','#ffffff'],
    ['#f97316','#fde047','#111827','#ffffff'],
    ['#e11d48','#8b5cf6','#ffffff','#0b0f14'],
    ['#14b8a6','#22d3ee','#0b0f14','#ffffff'],
    ['#60a5fa','#a78bfa','#0f172a','#ffffff'],
    ['#fda4af','#fb7185','#111827','#ffffff'],
    ['#86efac','#22c55e','#052e1a','#ffffff']
  ], []);

  const glyphs = useMemo(() => {
    const g = {
      bolt:'M58 0L26 54h24l-8 46 32-54H50l8-46z',
      spark:'M50 0L58 42L100 50L58 58L50 100L42 58L0 50L42 42Z',
      check:'M10 55 L40 85 L90 15 L78 10 L38 66 L22 50 Z',
      x:'M10 20 L20 10 L50 40 L80 10 L90 20 L60 50 L90 80 L80 90 L50 60 L20 90 L10 80 L40 50 Z',
      plus:'M45 10 H55 V45 H90 V55 H55 V90 H45 V55 H10 V45 H45 Z',
      minus:'M10 45 H90 V55 H10 Z',
      play:'M20 10 L85 50 L20 90 Z',
      pause:'M30 10 H45 V90 H30 Z M55 10 H70 V90 H55 Z',
      stop:'M25 25 H75 V75 H25 Z',
      forward:'M15 20 L50 50 L15 80 Z M50 20 L85 50 L50 80 Z',
      back:'M85 20 L50 50 L85 80 Z M50 20 L15 50 L50 80 Z',
      search:'M60 60 A25 25 0 1 1 61 60 L88 88 L82 94 Z',
      bell:'M50 10 C30 10 30 35 30 45 V60 L20 70 H80 L70 60 V45 C70 35 70 10 50 10 Z M45 80 A5 5 0 1 0 55 80 Z',
      camera:'M20 35 H35 L45 25 H65 L75 35 H80 A10 10 0 0 1 90 45 V75 H10 V45 A10 10 0 0 1 20 35 Z M50 42 A18 18 0 1 0 50 78 A18 18 0 1 0 50 42 Z',
      chat:'M10 20 H90 V65 H60 L45 80 L45 65 H10 Z',
      cloud:'M25 65 H80 A15 15 0 0 0 80 35 A20 20 0 0 0 45 30 A18 18 0 0 0 25 45 A12 12 0 0 0 25 65 Z',
      download:'M50 10 V65 M30 45 L50 65 L70 45 M20 80 H80',
      upload:'M50 90 V35 M30 55 L50 35 L70 55 M20 20 H80',
      edit:'M15 70 L70 15 L85 30 L30 85 L15 85 Z M65 20 L80 35',
      email:'M10 25 H90 V75 H10 Z M10 25 L50 55 L90 25',
      eye:'M10 50 C25 20 75 20 90 50 C75 80 25 80 10 50 Z M50 62 A12 12 0 1 0 50 38 A12 12 0 1 0 50 62 Z',
      flame:'M50 10 C70 30 60 40 70 55 C75 70 65 90 50 90 C35 90 25 75 30 60 C35 50 45 45 45 35 C45 25 40 20 50 10 Z',
      folder:'M10 30 H40 L50 40 H90 V80 H10 Z',
      globe:'M50 10 A40 40 0 1 1 49.9 10 Z M10 50 H90 M50 10 V90 M25 20 A60 60 0 0 0 25 80 M75 20 A60 60 0 0 1 75 80',
      grid:'M10 10 H90 V90 H10 Z M10 40 H90 M10 70 H90 M40 10 V90 M70 10 V90',
      link:'M35 35 A20 20 0 0 1 65 65 L55 55 M45 45 L65 25 A15 15 0 0 1 85 45 A20 20 0 0 1 65 65',
      lock:'M25 45 H75 V85 H25 Z M35 45 V35 A15 15 0 1 1 65 35 V45',
      mapPin:'M50 10 A22 22 0 0 1 72 32 C72 52 50 90 50 90 C50 90 28 52 28 32 A22 22 0 0 1 50 10 Z M50 24 A8 8 0 1 0 50 40 A8 8 0 1 0 50 24 Z',
      menu:'M15 25 H85 M15 50 H85 M15 75 H85',
      mic:'M50 15 A15 15 0 0 1 65 30 V55 A15 15 0 0 1 35 55 V30 A15 15 0 0 1 50 15 Z M35 55 C35 70 65 70 65 55 M50 70 V85 M35 85 H65',
      moon:'M65 15 A35 35 0 1 0 85 85 A25 25 0 1 1 65 15 Z',
      music:'M35 20 V72 A8 8 0 1 0 43 80 V35 L75 28 V60 A8 8 0 1 0 83 68 V16 Z',
      phone:'M25 15 L40 15 L45 35 L35 40 C40 60 60 80 80 85 L85 75 L65 70 L65 55 L85 60 L85 75 C65 95 25 65 25 15 Z',
      photo:'M10 30 H30 L35 20 H65 L70 30 H90 V80 H10 Z M40 40 A15 15 0 1 0 40 70 A15 15 0 1 0 40 40 Z',
      pie:'M50 50 V15 A35 35 0 1 1 15 50 Z M50 15 A35 35 0 0 1 85 50 H50 Z',
      pin:'M50 10 L55 45 L85 50 L55 55 L50 90 L45 55 L15 50 L45 45 Z',
      printer:'M25 25 H75 V45 H25 Z M25 55 H75 V85 H25 Z M35 20 H65',
      rocket:'M50 10 L65 35 L90 45 L65 55 L50 90 L35 55 L10 45 L35 35 Z',
      settings:'M50 20 L60 22 L68 15 L80 25 L73 32 L76 42 L88 48 L88 62 L76 68 L73 78 L80 85 L68 95 L60 88 L50 90 L40 88 L32 95 L20 85 L27 78 L24 68 L12 62 L12 48 L24 42 L27 32 L20 25 L32 15 L40 22 Z M50 40 A10 10 0 1 0 50 60 A10 10 0 1 0 50 40 Z',
      share:'M70 20 A10 10 0 1 1 69.9 20 L40 40 A10 10 0 1 0 40 60 L70 80 A10 10 0 1 1 69.9 80',
      shield:'M20 25 L50 15 L80 25 V55 C80 70 65 82 50 90 C35 82 20 70 20 55 Z',
      shieldCheck:'M20 25 L50 15 L80 25 V55 C80 70 65 82 50 90 C35 82 20 70 20 55 Z M35 55 L47 67 L70 40',
      cart:'M20 25 H85 L80 55 H32 L28 40 H15 Z M35 70 A6 6 0 1 0 35 82 A6 6 0 1 0 35 70 Z M70 70 A6 6 0 1 0 70 82 A6 6 0 1 0 70 70 Z',
      sliders:'M25 25 V85 M25 45 H60 M50 15 V85 M50 65 H85 M75 25 V85 M75 35 H40',
      sun:'M50 20 A15 15 0 1 1 49.9 20 Z M50 5 V15 M50 85 V95 M5 50 H15 M85 50 H95 M18 18 L25 25 M75 75 L82 82 M18 82 L25 75 M75 25 L82 18',
      tag:'M10 40 L40 10 L90 10 L90 60 L60 90 Z M75 25 A6 6 0 1 0 75 37 A6 6 0 1 0 75 25 Z',
      target:'M50 10 A40 40 0 1 1 49.9 10 Z M50 25 A25 25 0 1 0 50 75 A25 25 0 1 0 50 25 Z M50 40 A10 10 0 1 1 49.9 40 Z',
      trash:'M30 30 H70 V85 H30 Z M25 25 H75 L70 20 H30 Z M40 40 V75 M50 40 V75 M60 40 V75',
      trophy:'M30 25 H70 V35 H85 V45 C85 65 70 80 50 80 C30 80 15 65 15 45 V35 H30 Z M40 85 H60 V90 H40 Z',
      user:'M50 45 A15 15 0 1 0 49.9 45 Z M25 85 C25 65 75 65 75 85 Z',
      users:'M35 50 A12 12 0 1 0 34.9 50 Z M65 45 A10 10 0 1 0 64.9 45 Z M20 85 C20 70 50 70 50 85 Z M50 82 C50 68 80 68 80 82 Z',
      video:'M20 30 H60 V70 H20 Z M60 45 L85 35 V65 L60 55 Z',
      wifi:'M10 60 C30 40 70 40 90 60 M25 70 C40 55 60 55 75 70 M45 80 A5 5 0 1 0 55 80 A5 5 0 1 0 45 80 Z',
      wrench:'M30 20 A10 10 0 0 0 45 35 L25 55 L45 75 L65 55 A10 10 0 0 0 80 70 L90 90 L70 80 A20 20 0 0 1 30 40 Z',
      code:'M30 20 L15 50 L30 80 M70 20 L85 50 L70 80 M45 15 L55 85',
      heart:'M50 85 L15 50 A18 18 0 1 1 50 30 A18 18 0 1 1 85 50 Z',
      diamond:'M50 10 L90 50 L50 90 L10 50 Z',
      triangle:'M50 10 L90 90 H10 Z',
      rhombus:'M30 10 L70 30 L50 90 L10 70 Z',
      octagon:'M35 10 H65 L90 35 V65 L65 90 H35 L10 65 V35 Z',
      capsule:'M20 40 A20 20 0 0 1 40 20 H60 A20 20 0 0 1 80 40 V60 A20 20 0 0 1 60 80 H40 A20 20 0 0 1 20 60 Z',
      leaf:'M50 15 C80 20 90 50 85 75 C55 85 25 75 15 45 C30 30 40 25 50 15 Z',
      wave:'M10 60 Q30 40 50 60 T90 60',
      cube:'M30 20 L70 20 L90 40 L50 60 L10 40 Z M10 40 L10 80 L50 100 L50 60 Z M90 40 L90 80 L50 100',
      book:'M15 25 H80 V85 H15 Z M50 25 V85',
      calendar:'M20 30 H80 V80 H20 Z M30 20 V35 M70 20 V35 M20 45 H80',
      compass:'M50 15 A35 35 0 1 1 49.9 15 Z M40 60 L50 35 L60 60 L35 50 Z',
      crown:'M15 75 H85 L80 35 L65 55 L50 30 L35 55 L20 35 Z',
      feather:'M20 70 C40 40 60 30 80 30 C60 50 50 60 40 80 Z',
      key:'M60 20 A15 15 0 1 1 59.9 20 Z M50 30 L20 60 L25 65 L35 55 L40 60 L30 70',
      lightning:'M40 10 L70 10 L50 45 L70 45 L30 95 L45 55 L30 55 Z',
      magnet:'M25 25 H45 V55 H35 V35 H25 Z M55 25 H75 V35 H65 V55 H55 Z',
      palette:'M50 15 A35 35 0 1 1 49.9 15 Z M35 30 A5 5 0 1 0 36 30 Z M65 30 A5 5 0 1 0 66 30 Z M30 50 A5 5 0 1 0 31 50 Z M70 55 A5 5 0 1 0 71 55 Z',
      puzzle:'M20 40 H40 A8 8 0 1 1 40 56 H20 V80 H80 V56 H60 A8 8 0 1 1 60 40 H80 V20 H20 Z',
      rss:'M20 80 A10 10 0 1 0 21 80 Z M20 55 A25 25 0 0 1 45 80 M20 30 A50 50 0 0 1 70 80',
      scale:'M50 20 V80 M20 35 L50 45 L80 35 M20 35 L12 55 L38 55 Z M80 35 L72 55 L98 55 Z',
      throbber:'M50 15 A35 35 0 0 1 85 50 M85 50 A35 35 0 0 1 50 85',
      thermometer:'M50 25 A10 10 0 1 1 49.9 25 Z M50 30 V70 A15 15 0 1 0 50 70 Z',
      umbrella:'M10 55 A40 25 0 0 1 90 55 M50 55 V90 M25 55 Q50 35 75 55',
      anchor:'M50 15 V70 M30 70 A20 20 0 0 0 70 70 M40 30 H60',
      bulb:'M50 20 A20 20 0 1 1 49.9 20 Z M40 60 H60 M45 70 H55',
      chart:'M20 80 H90 V85 H20 Z M30 70 V80 H40 V50 H30 Z M45 80 H55 V40 H45 Z M60 80 H70 V60 H60 Z',
      trophy2:'M20 25 H80 V35 H90 V50 C90 70 70 85 50 85 C30 85 10 70 10 50 V35 H20 Z'
    };
    g.star5 = starPath(50,50,5,45,20);
    g.star6 = starPath(50,50,6,45,18);
    g.star8 = starPath(50,50,8,45,16);
    return g;
  }, []);

  // ---- state for controls (mirrors original DOM ids) ----
  const [brand, setBrand] = useState("Logo Text");
  const [tag, setTag] = useState("");
  const [font, setFont] = useState(fontStacks[0]);
  const [weight, setWeight] = useState(600);
  const [layout, setLayout] = useState('left');
  const [gap, setGap] = useState(20);
  const [shape, setShape] = useState('circle');
  const [size, setSize] = useState(120);
  const [radius, setRadius] = useState(24);
  const [stroke, setStroke] = useState(0);
  const [strokeCap, setStrokeCap] = useState('round');
  const [strokeJoin, setStrokeJoin] = useState('round');
  const [fillType, setFillType] = useState('solid');
  const [angle, setAngle] = useState(45);
  const [c1, setC1] = useState('#4cc9f0');
  const [c2, setC2] = useState('#a78bfa');
  const [cStroke, setCStroke] = useState('#0b0f14');
  const [cText, setCText] = useState('#ffffff');
  const [icon, setIcon] = useState('bolt');
  const [iconScale, setIconScale] = useState(64);
  const [iconStyle, setIconStyle] = useState('fill');
  const [iconStroke, setIconStroke] = useState(2);
  const [pad, setPad] = useState(28);
  const [w, setW] = useState(800);
  const [h, setH] = useState(400);
  const [bg, setBg] = useState('transparent');
  const [copied, setCopied] = useState(false);

  // ---- SVG building (ported) ----
  const svgMarkup = useMemo(() => {
    const gradId = uid();
    const o = { brand, tag, font, weight, layout, gap, shape, size, radius, stroke, strokeCap, strokeJoin, fillType, angle, c1, c2, cStroke, cText, icon, iconScale, iconStyle, iconStroke, pad, w, h, bg };
    const emblem = buildEmblem(o, gradId);
    const iconNode = buildIcon(o, glyphs);

    const brandSize = Math.round(o.size*0.36);
    const tagSize = Math.round(o.size*0.18);

    const textBrand = (o.layout==='icon') ? '' : `<text x="0" y="0" font-family="${escAttr(o.font)}" font-size="${brandSize}" font-weight="${o.weight}" fill="${o.cText}" dominant-baseline="hanging">${escText(o.brand)}</text>`;
    const textTag = (o.layout==='icon' || !o.tag) ? '' : `<text x="0" y="${brandSize+10}" font-family="${escAttr(o.font)}" font-size="${tagSize}" fill="${o.cText}" opacity="0.85" dominant-baseline="hanging">${escText(o.tag)}</text>`;

    const gp = o.layout==='icon' ? 0 : o.gap;
    let group = '';
    switch(o.layout){
      case 'left':
        group = `<g transform="translate(${o.pad},${o.h/2 - o.size/2})">${emblem}${iconNode}</g>`+
                `<g transform="translate(${o.pad + o.size + gp},${o.h/2 - (brandSize + (o.tag?10+tagSize:0))/2})">${textBrand}${textTag}</g>`; break;
      case 'right':
        group = `<g transform="translate(${o.w - o.pad - o.size},${o.h/2 - o.size/2})">${emblem}${iconNode}</g>`+
                `<g transform="translate(${o.pad},${o.h/2 - (brandSize + (o.tag?10+tagSize:0))/2})">${textBrand}${textTag}</g>`; break;
      case 'top':
        group = `<g transform="translate(${o.w/2 - o.size/2},${o.pad})">${emblem}${iconNode}</g>`+
                `<g transform="translate(${o.w/2 - Math.max(o.size*2, o.w*0.25)/2},${o.pad + o.size + gp})">${textBrand}${textTag}</g>`; break;
      case 'bottom':
        group = `<g transform="translate(${o.w/2 - Math.max(o.size*2, o.w*0.25)/2},${o.pad})">${textBrand}${textTag}</g>`+
                `<g transform="translate(${o.w/2 - o.size/2},${o.pad + (o.tag?brandSize+10+tagSize:brandSize) + gp})">${emblem}${iconNode}</g>`; break;
      case 'stacked-center':
        group = `<g transform="translate(${o.w/2 - o.size/2},${o.h/2 - (o.size+gp+brandSize)/2})">${emblem}${iconNode}</g>`+
                `<g transform="translate(${o.w/2 - Math.max(o.size*2, o.w*0.25)/2},${o.h/2 - (brandSize)/2 + o.size/2 + gp})">${textBrand}${textTag}</g>`; break;
      case 'stacked-left':
        group = `<g transform="translate(${o.pad},${o.pad})">${emblem}${iconNode}</g>`+
                `<g transform="translate(${o.pad},${o.pad + o.size + gp})">${textBrand}${textTag}</g>`; break;
      case 'stacked-right':
        group = `<g transform="translate(${o.w - o.pad - o.size},${o.pad})">${emblem}${iconNode}</g>`+
                `<g transform="translate(${o.w - o.pad - Math.max(o.size*2, o.w*0.25)},${o.pad + o.size + gp})">${textBrand}${textTag}</g>`; break;
      case 'badge-center':
        group = `<g transform="translate(${o.w/2 - o.size/2},${o.h/2 - o.size/2})">${emblem}${iconNode}</g>`+
                `<g transform="translate(${o.w/2 - Math.max(o.size*2, o.w*0.25)/2},${o.h/2 + o.size/2 + gp})">${textBrand}${textTag}</g>`; break;
      case 'badge-left':
        group = `<g transform="translate(${o.pad},${o.h/2 - o.size/2})">${emblem}${iconNode}</g>`+
                `<g transform="translate(${o.pad},${o.h/2 + o.size/2 + gp})">${textBrand}${textTag}</g>`; break;
      case 'badge-right':
        group = `<g transform="translate(${o.w - o.pad - o.size},${o.h/2 - o.size/2})">${emblem}${iconNode}</g>`+
                `<g transform="translate(${o.w - o.pad - Math.max(o.size*2, o.w*0.25)},${o.h/2 + o.size/2 + gp})">${textBrand}${textTag}</g>`; break;
      case 'split-50-50':
        group = `<g transform="translate(${o.pad},${o.h/2 - o.size/2})">${emblem}${iconNode}</g>`+
                `<g transform="translate(${o.w/2 + gp/2},${o.h/2 - (brandSize + (o.tag?10+tagSize:0))/2})">${textBrand}${textTag}</g>`; break;
      case 'split-40-60':
        group = `<g transform="translate(${o.pad},${o.h/2 - o.size/2})">${emblem}${iconNode}</g>`+
                `<g transform="translate(${o.w*0.4 + gp},${o.h/2 - (brandSize + (o.tag?10+tagSize:0))/2})">${textBrand}${textTag}</g>`; break;
      case 'overlap':
        group = `<g transform="translate(${o.w/2 - o.size/2 - brandSize/4},${o.h/2 - o.size/2})" opacity="0.95">${emblem}${iconNode}</g>`+
                `<g transform="translate(${o.w/2 - brandSize/2},${o.h/2 - brandSize/2})">${textBrand}</g>`; break;
      default:
        group = `<g transform=\"translate(${o.w/2 - o.size/2},${o.h/2 - o.size/2})\">${emblem}${iconNode}</g>`;
    }

    const defs = buildDefs(o, gradId);
    const rectBG = o.bg && o.bg!=='transparent' ? `<rect width="100%" height="100%" fill="${o.bg}"/>` : '';
    return `<?xml version="1.0" encoding="UTF-8"?>\n<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${o.w} ${o.h}" width="${o.w}" height="${o.h}">\n${defs}\n${rectBG}\n${group}\n</svg>`;
  }, [brand, tag, font, weight, layout, gap, shape, size, radius, stroke, strokeCap, strokeJoin, fillType, angle, c1, c2, cStroke, cText, icon, iconScale, iconStyle, iconStroke, pad, w, h, bg, glyphs]);

  useEffect(() => { if (!copied) return; const t = setTimeout(() => setCopied(false), 1000); return () => clearTimeout(t); }, [copied]);

  // ---- actions ----
  const doCopy = () => { navigator.clipboard.writeText(svgMarkup).then(() => setCopied(true)); };
  const doDownloadSVG = () => {
    const blob = new Blob([svgMarkup], { type: 'image/svg+xml' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'logo.svg'; a.click(); URL.revokeObjectURL(a.href);
  };
  const doDownloadPNG = () => {
    const img = new Image();
    const svg64 = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgMarkup)));
    img.onload = () => {
      const c = document.createElement('canvas'); c.width = w; c.height = h; const ctx = c.getContext('2d');
      if (bg && bg !== 'transparent') { ctx.fillStyle = bg; ctx.fillRect(0,0,w,h); }
      ctx.drawImage(img,0,0);
      c.toBlob(b => { const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = 'logo.png'; a.click(); URL.revokeObjectURL(a.href); }, 'image/png');
    };
    img.src = svg64;
  };
  const randomize = () => {
    const p = palettes[Math.floor(Math.random()*palettes.length)];
    setC1(p[0]); setC2(p[1]); setCText(p[2]); setCStroke(p[3]);
    const keys = Object.keys(glyphs);
    setIcon(keys[Math.floor(Math.random()*keys.length)]);
    setLayout(layouts[Math.floor(Math.random()*layouts.length)]);
  };

  // ---- UI ----
  return (
    <div className="app">
      <style>{css}</style>
      <header>
        <h1 className="title">🪄 SVG Logo Generator — Pro</h1>
        <div className="toolbar">
          <button className="btn" onClick={randomize}>Randomize</button>
          <button className="btn acc" onClick={doCopy}>{copied? 'Copied' : 'Copy SVG'}</button>
          <button className="btn ok" onClick={doDownloadSVG}>Download SVG</button>
          <button className="btn" onClick={doDownloadPNG}>Download PNG</button>
        </div>
      </header>

      <aside className="side" aria-label="Controls">
        <h3>Brand</h3>
        <div className="row"><label>Text</label><input type="text" value={brand} onChange={e=>setBrand(e.target.value)} /></div>
        <div className="row"><label>Tagline</label><input type="text" value={tag} onChange={e=>setTag(e.target.value)} placeholder="optional"/></div>
        <div className="row grid">
          <div className="row"><label>Font</label>
            <select value={font} onChange={e=>setFont(e.target.value)}>
              {fontStacks.map(f => <option key={f} value={f}>{f.split(',')[0]}</option>)}
            </select>
          </div>
          <div className="row"><label>Weight</label>
            <select value={weight} onChange={e=>setWeight(Number(e.target.value))}>{[400,500,600,700,800,900].map(n=> <option key={n} value={n}>{n}</option>)}</select>
          </div>
        </div>
        <div className="row grid">
          <div className="row"><label>Layout</label>
            <select value={layout} onChange={e=>setLayout(e.target.value)}>{layouts.map(l=> <option key={l} value={l}>{l}</option>)}</select>
          </div>
          <div className="row"><label>Gap</label><input className="tight" type="number" value={gap} min={0} max={160} onChange={e=>setGap(Number(e.target.value))}/></div>
        </div>

        <h3>Emblem</h3>
        <div className="row grid">
          <div className="row"><label>Shape</label>
            <select value={shape} onChange={e=>setShape(e.target.value)}>
              {['circle','rounded','hex','shield','none'].map(s=> <option key={s} value={s}>{s[0].toUpperCase()+s.slice(1)}</option>)}
            </select>
          </div>
        </div>
        <div className="row grid">
          <div className="row"><label>Size</label><input className="tight" type="number" value={size} min={40} max={320} onChange={e=>setSize(Number(e.target.value))}/></div>
        </div>
        <div className="row grid">
          <div className="row"><label>Corner</label><input className="tight" type="number" value={radius} min={0} max={80} onChange={e=>setRadius(Number(e.target.value))}/></div>
          <div className="row"><label>Stroke</label><input className="tight" type="number" value={stroke} min={0} max={24} onChange={e=>setStroke(Number(e.target.value))}/></div>
        </div>
        <div className="row grid">
          <div className="row"><label>Stroke cap</label><select value={strokeCap} onChange={e=>setStrokeCap(e.target.value)}>{['butt','round','square'].map(x=> <option key={x}>{x}</option>)}</select></div>
          <div className="row"><label>Stroke join</label><select value={strokeJoin} onChange={e=>setStrokeJoin(e.target.value)}>{['miter','round','bevel'].map(x=> <option key={x}>{x}</option>)}</select></div>
        </div>
        <div className="row grid">
          <div className="row"><label>Fill</label>
            <select value={fillType} onChange={e=>setFillType(e.target.value)}>
              <option value="solid">Solid</option>
              <option value="linear">Linear</option>
              <option value="radial">Radial</option>
            </select>
          </div>
          <div className="row"><label>Angle</label><input className="tight" type="number" value={angle} min={0} max={360} onChange={e=>setAngle(Number(e.target.value))}/></div>
        </div>

        <h3>Colors (live)</h3>
        <div className="row grid">
          <div className="row"><label>Primary</label><input type="color" value={c1} onChange={e=>setC1(e.target.value)}/><input type="text" value={c1} onChange={e=>setC1(e.target.value)}/></div>
        </div>
        <div className="row grid">
          <div className="row"><label>Secondary</label><input type="color" value={c2} onChange={e=>setC2(e.target.value)}/><input type="text" value={c2} onChange={e=>setC2(e.target.value)}/></div>
        </div>
        <div className="row grid">
          <div className="row"><label>Stroke col</label><input type="color" value={cStroke} onChange={e=>setCStroke(e.target.value)}/><input type="text" value={cStroke} onChange={e=>setCStroke(e.target.value)}/></div>
        </div>
        <div className="row grid">
          <div className="row"><label>Text col</label><input type="color" value={cText} onChange={e=>setCText(e.target.value)}/><input type="text" value={cText} onChange={e=>setCText(e.target.value)}/></div>
        </div>
        <div className="row"><div className="swatch">
          {palettes.map((p,i)=> (
            <button key={i} style={{background:p[0]}} title={`Primary ${p[0]} / Secondary ${p[1]}`}
              onClick={()=>{ setC1(p[0]); setC2(p[1]); setCText(p[2]); setCStroke(p[3]); }} />
          ))}
        </div></div>

        <h3>Icon</h3>
        <div className="row grid">
          <div className="row"><label>Glyph (50+)</label>
            <select value={icon} onChange={e=>setIcon(e.target.value)}>
              {Object.keys(glyphs).map(k=> <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div className="row"><label>Scale</label><input className="tight" type="number" value={iconScale} min={24} max={180} onChange={e=>setIconScale(Number(e.target.value))}/></div>
        </div>
        <div className="row grid">
          <div className="row"><label>Icon style</label><select value={iconStyle} onChange={e=>setIconStyle(e.target.value)}><option>fill</option><option>stroke</option></select></div>
          <div className="row"><label>Icon stroke</label><input className="tight" type="number" value={iconStroke} min={1} max={12} onChange={e=>setIconStroke(Number(e.target.value))}/></div>
        </div>

        <h3>Canvas</h3>
        <div className="row grid">
          <div className="row"><label>Padding</label><input className="tight" type="number" value={pad} min={0} max={160} onChange={e=>setPad(Number(e.target.value))}/></div>
          <div className="row"><label>Canvas W</label><input className="tight" type="number" value={w} min={200} max={2200} onChange={e=>setW(Number(e.target.value))}/></div>
        </div>
        <div className="row grid">
          <div className="row"><label>Canvas H</label><input className="tight" type="number" value={h} min={200} max={2200} onChange={e=>setH(Number(e.target.value))}/></div>
          <div className="row"><label>BG</label><input type="text" value={bg} onChange={e=>setBg(e.target.value)}/></div>
        </div>
        <p className="hint">Tip: click a palette swatch to apply both primary/secondary quickly.</p>
      </aside>

      <section className="main">
        <div className="preview" aria-label="Logo preview">
          {/* eslint-disable-next-line react/no-danger */}
          <div dangerouslySetInnerHTML={{__html: svgMarkup}} />
        </div>
        <div className="out">
          <div className="card"><h4>SVG Markup</h4><textarea value={svgMarkup} readOnly spellCheck={false}></textarea><div className="toolbar"><button className="btn acc" onClick={doCopy}>{copied? 'Copied' : 'Copy'}</button></div></div>
          <div className="card"><h4>Notes</h4><textarea readOnly value={"• 50+ glyphs, 25+ font stacks, 12 layouts\n• Live color pickers (primary/secondary/text/stroke) + palette\n• Icon can render as fill or stroke\n• Linear/Radial gradients with angle\n• Export to SVG or PNG"}></textarea></div>
        </div>
      </section>
    </div>
  );
}

// ----- helpers (ported) -----
function uid(){ return 'g'+Math.random().toString(36).slice(2,8); }
function escAttr(s){ return String(s).replace(/"/g,'&quot;'); }
function escText(s){ return String(s).replace(/[&<>]/g, c=>({'&':'&amp;','<':'&lt;','>':'&gt;'}[c])); }
function starPath(cx, cy, points, outerR, innerR){ let d=''; const step=Math.PI/points; for(let i=0;i<2*points;i++){ const r=(i%2===0)?outerR:innerR; const a=i*step - Math.PI/2; const x=cx + r*Math.cos(a); const y=cy + r*Math.sin(a); d += (i===0?'M':'L') + x + ' ' + y + ' '; } return d+'Z'; }
function hexPoints(s){ const r=s/2, cx=r, cy=r, a=Math.PI/3; const pts=[]; for(let i=0;i<6;i++){ const ang=a*i + Math.PI/6; pts.push([cx + r*Math.cos(ang), cy + r*Math.sin(ang)]);} return pts.map(p=>p[0].toFixed(2)+','+p[1].toFixed(2)).join(' '); }
function shieldPath(s){ const w=s,h=s; const t=h*0.18; const b=h*0.14; const r=w*0.18; return `M ${r} ${t} Q ${w/2} 0 ${w-r} ${t} L ${w-r} ${h*0.55} Q ${w/2} ${h-b} ${r} ${h*0.55} Z`; }
function buildDefs(o, gradId){ if(o.fillType==='solid') return ''; if(o.fillType==='linear'){ const a=(o.angle%360)*Math.PI/180; const x=(Math.cos(a)+1)/2, y=(Math.sin(a)+1)/2; const x2=1-x, y2=1-y; return `<defs><linearGradient id="${gradId}" x1="${x}" y1="${y}" x2="${x2}" y2="${y2}"><stop offset="0%" stop-color="${o.c1}"/><stop offset="100%" stop-color="${o.c2}"/></linearGradient></defs>`;} return `<defs><radialGradient id="${gradId}" cx="50%" cy="50%" r="70%"><stop offset="0%" stop-color="${o.c1}"/><stop offset="100%" stop-color="${o.c2}"/></radialGradient></defs>`; }
function buildEmblem(o, gradId){ const fill=o.fillType==='solid'? o.c1 : `url(#${gradId})`; const s=o.size, r=Math.min(o.radius, s/2); const stroke=o.stroke>0 ? ` stroke="${o.cStroke}" stroke-width="${o.stroke}" stroke-linecap="${o.strokeCap}" stroke-linejoin="${o.strokeJoin}"` : ''; let node=''; if(o.shape==='circle') node = `<circle cx="${s/2}" cy="${s/2}" r="${s/2}" fill="${fill}"${stroke}/>`; if(o.shape==='rounded') node = `<rect x="0" y="0" width="${s}" height="${s}" rx="${r}" ry="${r}" fill="${fill}"${stroke}/>`; if(o.shape==='hex') node = `<polygon points="${hexPoints(s)}" fill="${fill}"${stroke}/>`; if(o.shape==='shield') node = `<path d="${shieldPath(s)}" fill="${fill}"${stroke}/>`; if(o.shape==='none') node = ''; return `<g>${node}</g>`; }
function buildIcon(o, map){ if(o.icon==='none') return ''; const s=o.size; const scale=o.iconScale/100; const size=s*scale; const x=s/2 - size/2, y=s/2 - size/2; const color=o.c2 || '#fff'; const p=map[o.icon] || ''; if(!p) return ''; if(o.iconStyle==='stroke'){ return `<g transform="translate(${x},${y}) scale(${size/100})"><path d="${p}" stroke="${color}" stroke-width="${o.iconStroke}" fill="none" stroke-linecap="round" stroke-linejoin="round"/></g>`; } return `<g transform="translate(${x},${y}) scale(${size/100})"><path d="${p}" fill="${color}"/></g>`; }
